import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  IconButton,
  Paper,
  Typography,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';

import { addHospital, getHospitalById, updateHospital } from '../services/HospitalService';
import FormField from '../components/patients/FormField';

const idPattern = /^[a-z_]+$/;
const minIdLength = 3;
const minNameLength = 3;

const SetHospitalData = () => {
  const navigate = useNavigate();
  const { hospitalId } = useParams();
  const isNew = !hospitalId;

  const [formData, setFormData] = useState({ id: '', name: '', shortName: '' });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(!isNew);
  const [submitting, setSubmitting] = useState(false);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    if (isNew) return;

    const load = async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const data = await getHospitalById(hospitalId);
        if (!data) throw new Error('Hospital no válido');
        setFormData({
          id: data.id,
          name: data.name ?? '',
          shortName: data.shortName ?? '',
        });
      } catch (e) {
        setLoadError(e.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [hospitalId, isNew]);

  const onFieldChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined, submitForm: undefined }));
  };

  const validate = () => {
    const next = {};
    const id = formData.id.trim();
    const name = formData.name.trim();
    const shortName = formData.shortName.trim();

    if (isNew) {
      if (!id) {
        next.id = 'Campo obligatorio';
      } else if (id.length < minIdLength) {
        next.id = `Debe contener al menos ${minIdLength} caracteres`;
      } else if (!idPattern.test(id)) {
        next.id = 'Solo minúsculas y guiones bajos';
      }
    }

    if (!name) {
      next.name = 'Campo obligatorio';
    } else if (name.length < minNameLength) {
      next.name = `Debe contener al menos ${minNameLength} caracteres`;
    }

    if (!shortName) {
      next.shortName = 'Campo obligatorio';
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    setErrors((prev) => ({ ...prev, submitForm: undefined }));
    try {
      if (isNew) {
        const newId = await addHospital({
          id: formData.id.trim(),
          name: formData.name.trim(),
          shortName: formData.shortName.trim(),
        });
        navigate(`/hospital/view/${newId}`);
      } else {
        await updateHospital(hospitalId, {
          name: formData.name.trim(),
          shortName: formData.shortName.trim(),
        });
        navigate(`/hospital/view/${hospitalId}`);
      }
    } catch (e) {
      setErrors((prev) => ({ ...prev, submitForm: e.message || 'Error al guardar' }));
      setSubmitting(false);
    }
  };

  const handleBack = () => navigate(-1);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (loadError) {
    return (
      <Container sx={{ pt: 4 }}>
        <Typography color="error">{loadError}</Typography>
        <Button sx={{ mt: 2 }} onClick={() => navigate('/hospitals/')}>
          Volver a hospitales
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ pt: 4, pb: 8 }}>
      <Paper elevation={3} sx={{ padding: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <IconButton onClick={handleBack} aria-label="back">
            <ArrowBack color="primary" sx={{ fontSize: 40 }} />
          </IconButton>
          <Typography variant="h5" align="center" sx={{ flexGrow: 1 }}>
            {isNew ? 'Añadir hospital' : 'Editar hospital'}
          </Typography>
        </Box>

        <Box component="form" onSubmit={handleSubmit} noValidate>
          <FormField
            label="ID"
            name="id"
            type="text"
            value={formData.id}
            onChange={onFieldChange}
            readOnly={!isNew}
            error={errors.id}
            helperText={
              isNew
                ? 'Solo minúsculas y guiones bajos. No se podrá modificar. Ejemplo: "Hospital Universitario de Navarra" → "hsp_uni_nav".'
                : 'El ID no se puede modificar.'
            }
          />
          <FormField
            label="Nombre completo"
            name="name"
            type="text"
            value={formData.name}
            onChange={onFieldChange}
            error={errors.name}
          />
          <FormField
            label="Nombre corto"
            name="shortName"
            type="text"
            value={formData.shortName}
            onChange={onFieldChange}
            error={errors.shortName}
            helperText='Para mejorar la legibilidad en tablas. Ejemplo: "Hospital Universitario de Navarra" → "HU Navarra".'
          />

          {errors.submitForm && (
            <Typography color="error" sx={{ mt: 2 }}>
              {errors.submitForm}
            </Typography>
          )}

          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button onClick={handleBack} disabled={submitting}>
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={submitting}
              sx={{ minWidth: 140 }}
            >
              {submitting ? (
                <CircularProgress size={24} color="inherit" />
              ) : isNew ? (
                'Crear hospital'
              ) : (
                'Guardar'
              )}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default SetHospitalData;
